/* eslint-disable complexity */
import React, {
    useState,
    createContext,
    ReactNode,
    useRef,
    useMemo
} from "react";

import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlassian/wac-ufo-segment";

import { Adg4Button } from "@/components/adg4/button/src";
import { Video } from "@/components/wpl/video/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

import {
    ProductMarkConfluence,
    ProductMarkJPD,
    ProductMarkJSM,
    ProductMarkJira,
    ProductMarkLoom,
    SquiggleLineAccent1,
    SquiggleLineAccent2,
    ArrowGroupAccent,
    StarGroupAccent,
    LightningAccent1
} from "./illustrations";
import { Sparkles } from "./illustrations";
import { HomepageSectionScrollItem } from "./scroll-item";
export type ScrollContextType = {
    activeAccentImg: ReactNode | null;
    activeBrand: string | null;
    activeColor: string | null;
    activeProductCTAText: string | null;
    activeProductCTAUrl: string | null;
    activeCustomerCTAUrl: string | null;
    activeFeatureVideo: string | null;
    activePosterImage: string | null;
    activeIndex: number | 0;
    activeShape: string | null;
    setActiveAccentImg: (newValue: JSX.Element) => void;
    setActiveBrand: (newValue: string) => void;
    setActiveColor: (newValue: string) => void;
    setActiveProductCTAText: (newValue: string) => void;
    setActiveProductCTAUrl: (newValue: string) => void;
    setActiveCustomerCTAUrl: (newValue: string) => void;
    setActiveFeatureVideo: (newValue: string) => void;
    setActivePosterImage: (newValue: string) => void;
    setActiveIndex: (newValue: number) => void;
    setActiveShape: React.Dispatch<React.SetStateAction<string | null>>;
};
const ScrollContextState = {
    activeAccentImg: null,
    activeBrand: null,
    activeColor: null,
    activeProductCTAText: null,
    activeProductCTAUrl: null,
    activeCustomerCTAUrl: null,
    activeFeatureVideo: null,
    activePosterImage: null,
    activeIndex: 0,
    activeShape: null,
    setActiveAccentImg: () => undefined,
    setActiveBrand: () => undefined,
    setActiveColor: () => undefined,
    setActiveProductCTAText: () => undefined,
    setActiveProductCTAUrl: () => undefined,
    setActiveCustomerCTAUrl: () => undefined,
    setActiveFeatureVideo: () => undefined,
    setActivePosterImage: () => undefined,
    setActiveIndex: () => 0,
    setActiveShape: () => undefined
};
export const ScrollContext =
    createContext<ScrollContextType>(ScrollContextState);

interface ProductCTAInterface {
    text: string;
    url: string;
}

export type TeamType = {
    index?: number;
    productName: string;
    logo: ReactNode;
    logoBGColor?: string;
    productCTA: ProductCTAInterface;
    customerCTAUrl: string;
    header: string;
    description: string;
    quote: string;
    brand: string;
    name: string;
    title: string;
    portrait: string;
    featureVideo: string;
    posterImage: string;
    bgColor: string;
    bgShape: string;
    accentImg: JSX.Element;
};
export const ScrollPinnedSections = () => {
    const intl = useIntl();
    const Teams: TeamType[] = [
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.ProductName",
                defaultMessage: "Jira"
            }),
            logo: <ProductMarkJira />,
            productCTA: {
                text: intl.formatMessage({
                    id: "HomePage.ScrollPinnedSection.Jira.Cta",
                    defaultMessage: "Get started with Jira"
                }),
                url: useLocalizedHref("/software/jira")
            },
            header: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.Header",
                defaultMessage: "Dream it, plan it, launch it"
            }),
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.Description",
                defaultMessage:
                    "The #1 tool for agile teams is now for all teams. Plan, track, and deliver your biggest ideas together."
            }),
            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.Quote",
                defaultMessage:
                    "We've been able to reduce our cycle time and increase throughput by 200%, which means we're delivering capabilities to customers faster and increasing customer satisfaction."
            }),
            customerCTAUrl: useLocalizedHref("/customers/lumen"),
            name: "Mai Lian Scarlett",
            title: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.JobTitle",
                defaultMessage:
                    "Former Senior Director, Agile Transformation Office, Lumen"
            }),
            brand: "https://wac-cdn.atlassian.com/misc-assets/webp-images/lumen.webp",
            portrait:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/People-profile.webp",
            featureVideo:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Jira_LowBR.mp4",
            posterImage:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Jira-4003614779.webp",
            accentImg: useMemo(() => <SquiggleLineAccent1 />, []),
            bgColor: "82b535",
            bgShape:
                "0% 32%, 2% 97%, 56% 100%, 56% 96%, 100% 96%, 100% 0%, 55.5% 2%, 56.5% 18%"
        },
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.ProductName",
                defaultMessage: "Confluence"
            }),
            logo: <ProductMarkConfluence />,
            productCTA: {
                text: intl.formatMessage({
                    id: "HomePage.ScrollPinnedSection.Confluence.Cta",
                    defaultMessage: "Get started with Confluence"
                }),
                url: `${useLocalizedHref("/software/confluence")}`
            },
            header: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.Header",
                defaultMessage: "Scale your knowledge"
            }),
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.Description",
                defaultMessage:
                    "Connect and consolidate scattered docs and disconnected teammates in one, central source of truth."
            }),
            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.Quote",
                defaultMessage:
                    "The big win that we've seen through the implementation of Confluence Cloud is definitely collaboration. It's a very open way of working."
            }),
            customerCTAUrl: useLocalizedHref("/customers/dropbox"),
            name: "Damian Davis",
            title: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.JobTitle",
                defaultMessage: "Lead Atlassian Administrator, Dropbox"
            }),
            brand: "https://wac-cdn.atlassian.com/misc-assets/webp-images/dropbox.webp",
            portrait:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/damien-davis.webp",
            featureVideo:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ Confluence_LowBR.mp4",
            posterImage:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ Confluence-2040505915.webp",
            accentImg: useMemo(() => <ArrowGroupAccent />, []),
            bgColor: "BF63F3",
            bgShape:
                "0% 1%, 100% 0%, 100% 33%, 55% 33%, 55% 100%, 3% 96%, 2% 32%, 0% 32%"
        },
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.ProductName",
                defaultMessage: "Jira Service Management"
            }),
            logo: <ProductMarkJSM />,
            productCTA: {
                text: intl.formatMessage({
                    id: "HomePage.ScrollPinnedSection.JSM.Cta",
                    defaultMessage: "Get started with Jira Service Management"
                }),
                url: `${useLocalizedHref("/software/jira/service-management")}`
            },
            header: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.Header",
                defaultMessage: "Deliver service faster"
            }),
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.Description",
                defaultMessage:
                    "Connect Dev and Ops teams on a single platform with customizable features and AI-powered agents."
            }),
            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.Quote",
                defaultMessage:
                    "Atlassian Intelligence acts as a virtual teammate summarizing 5-page PIR reports into 5-sentence recaps for me. It’s already proven its worth by improving productivity across our product teams."
            }),
            customerCTAUrl: useLocalizedHref("/customers/dominos"),
            name: "Matthias Hansen",
            title: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.JobTitle",
                defaultMessage: "Group CTO, Domino's Pizza Enterprises"
            }),
            brand: "https://wac-cdn.atlassian.com/misc-assets/webp-images/dominos.webp",
            portrait:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/matthias-hansen.webp",
            featureVideo:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ JSM_LowBR.mp4",
            posterImage:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ JSM-3020245023.webp",
            accentImg: useMemo(() => <LightningAccent1 />, []),
            bgColor: "FCA700",
            bgShape:
                "0% 41%, 8% 41%, 10% 21%, 65% 13%, 67% 3%, 100% 0%, 98% 27%, 100% 27%, 100% 100%, 0% 98%"
        },
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.ProductName",
                defaultMessage: "Loom"
            }),
            logo: <ProductMarkLoom />,
            productCTA: {
                text: intl.formatMessage({
                    id: "HomePage.ScrollPinnedSection.Loom.Cta",
                    defaultMessage: "Get started with Loom"
                }),
                url: "https://www.loom.com/"
            },
            header: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.Header",
                defaultMessage: "Record, share, collaborate"
            }),
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.Description",
                defaultMessage:
                    "Easily record and share AI-powered video messages to reach a broader audience for better async collaboration."
            }),
            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.Quote",
                defaultMessage:
                    "Loom has been the light of my life since you showed me it. I never tire of hearing this from folks."
            }),
            customerCTAUrl: useLocalizedHref("/customers"),
            name: "Alexis Ohanian",
            title: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.JobTitle",
                defaultMessage: "Founder, Seven Seven Six"
            }),
            brand: "https://wac-cdn.atlassian.com/misc-assets/webp-images/seven-seven-six.webp",
            portrait:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/alexis-ohanian.webp",
            featureVideo:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ Loom_LowBR.mp4",
            posterImage:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ Loom-791474346.webp",
            accentImg: useMemo(() => <SquiggleLineAccent2 />, []),
            bgColor: "FFF",
            bgShape: ""
        },
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.ProductName",
                defaultMessage: "Jira Product Discovery"
            }),
            logo: <ProductMarkJPD />,
            productCTA: {
                text: intl.formatMessage({
                    id: "HomePage.ScrollPinnedSection.JPD.Cta",
                    defaultMessage: "Get started with Jira Product Discovery"
                }),
                url: `${useLocalizedHref("/software/jira/product-discovery")}`
            },
            header: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.Header",
                defaultMessage: "Ideate faster than ever"
            }),
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.Description",
                defaultMessage:
                    "Capture and prioritize ideas and align everyone with product roadmaps - all in one single Jira platform."
            }),
            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.Quote",
                defaultMessage:
                    "Jira Product Discovery translated our entire process into a highly transparent workflow, connected to the work we were already doing in Jira. That was a mind-blowing moment."
            }),
            customerCTAUrl: useLocalizedHref("/customers/doodle"),
            name: "Stephanie Leue",
            title: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.JobTitle",
                defaultMessage: "Former Chief Product Officer, Doodle"
            }),
            brand: "https://wac-cdn.atlassian.com/misc-assets/webp-images/doodle.webp",
            portrait:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/stephanie-leue.webp",
            featureVideo:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ JPD_LowBR.mp4",
            posterImage:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ JPD-286968629.webp",
            accentImg: useMemo(() => <StarGroupAccent />, []),
            bgColor: "82B536",
            bgShape:
                "0% 20%, 48% 10%, 48% 2%, 100% 0%, 100% 97%, 55% 100%, 55% 96%, 2% 95%"
        }
    ];
    const imgSectionRef = useRef<HTMLDivElement>(null);

    const defaultFeatureVideo: string = Teams[0]?.featureVideo
        ? Teams[0]?.featureVideo
        : "";
    const defaultFeaturePosterImage: string = Teams[0]?.featureVideo
        ? Teams[0]?.posterImage
        : "";
    const defaultProductCTAText: string = Teams[0]?.productCTA.text
        ? Teams[0]?.productCTA.text
        : "";
    const defaultProductCTAUrl: string = Teams[0]?.productCTA.url
        ? Teams[0]?.productCTA.url
        : "";
    const defaultBgShape: string = Teams[0]?.bgShape ? Teams[0]?.bgShape : "";
    const defaultBgColor: string = Teams[0]?.bgColor ? Teams[0]?.bgColor : "";

    const { ref: scrollRef, inView: sectionIsVisible } = useInView({
        threshold: 0.15
    });
    const { ref: headerScrollRef, inView: headerIsVisible } = useInView({
        threshold: 1
    });
    const [activeAccentImg, setActiveAccentImg] = useState<JSX.Element>();
    const [activeBrand, setActiveBrand] = useState<string>("");
    const [activeColor, setActiveColor] = useState<string>(defaultBgColor);
    const [activeProductCTAText, setActiveProductCTAText] = useState<string>(
        defaultProductCTAText
    );
    const [activeProductCTAUrl, setActiveProductCTAUrl] =
        useState<string>(defaultProductCTAUrl);
    const [activeCustomerCTAUrl, setActiveCustomerCTAUrl] =
        useState<string>("");
    const [activeFeatureVideo, setActiveFeatureVideo] =
        useState<string>(defaultFeatureVideo);
    const [activePosterImage, setActivePosterImage] = useState<string>(
        defaultFeaturePosterImage
    );
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [activeShape, setActiveShape] = useState<string | null>(
        defaultBgShape
    );

    return (
        <section>
            <ScrollContext.Provider
                value={{
                    activeAccentImg,
                    activeBrand,
                    activeColor,
                    activeProductCTAText,
                    activeProductCTAUrl,
                    activeCustomerCTAUrl,
                    activeFeatureVideo,
                    activePosterImage,
                    activeIndex,
                    activeShape,
                    setActiveAccentImg,
                    setActiveBrand,
                    setActiveColor,
                    setActiveFeatureVideo,
                    setActivePosterImage,
                    setActiveIndex,
                    setActiveShape,
                    setActiveProductCTAText,
                    setActiveProductCTAUrl,
                    setActiveCustomerCTAUrl
                }}
            >
                <UFOSegment name="homepage-v2-scrollPinnedSection">
                    <div
                        css={scrollerStyles}
                        ref={scrollRef}
                        className={sectionIsVisible ? "is-visible" : "hidden"}
                    >
                        <Row>
                            <div className="row-width">
                                <div className="header-wrapper">
                                    <div
                                        css={css({
                                            insetBlockStart: "0",
                                            display: "flex",
                                            flexDirection: "column"
                                        })}
                                    >
                                        <div className="accent-heading">
                                            <h2
                                                ref={headerScrollRef}
                                                className={
                                                    headerIsVisible
                                                        ? "is-visible"
                                                        : "hidden"
                                                }
                                            >
                                                <Sparkles />
                                                {intl.formatMessage({
                                                    id: "HomePage.ScrollPinnedSectionHeader",
                                                    defaultMessage:
                                                        "Teamwork solutions for high-performing teams"
                                                })}
                                            </h2>
                                        </div>
                                        <div
                                            ref={imgSectionRef}
                                            className="half img-section"
                                            css={css({
                                                width: "calc(65% - 15px)",
                                                alignSelf: "flex-end",
                                                minHeight: 0
                                            })}
                                        >
                                            <div className="img-region">
                                                <div
                                                    className={`bg-shape ${
                                                        activeIndex % 2 == 0
                                                            ? "even"
                                                            : "odd"
                                                    }`}
                                                    style={{
                                                        clipPath: `polygon(${activeShape})`,
                                                        backgroundColor: `#${activeColor}`
                                                    }}
                                                />
                                                <div className="img-accent">
                                                    {activeAccentImg}
                                                </div>
                                                <Video
                                                    videoLink={
                                                        activeFeatureVideo
                                                    }
                                                    videoType="mp4"
                                                    className={`img-screenshot ${
                                                        activeIndex % 2 == 0
                                                            ? "even"
                                                            : "odd"
                                                    }`}
                                                    videoPreviewImage={
                                                        activePosterImage
                                                    }
                                                    aspectRatio="16/9"
                                                    loop={false}
                                                />
                                            </div>
                                            <Adg4Button
                                                variant="primary"
                                                color="blue"
                                                href={activeProductCTAUrl}
                                                dataEventProps={{
                                                    "data-product-key": "home",
                                                    "data-event": "clicked",
                                                    "data-event-component":
                                                        "link",
                                                    "data-event-container":
                                                        "scrollPinnedSection",
                                                    "data-label":
                                                        activeProductCTAText,
                                                    "data-label-english":
                                                        activeProductCTAText
                                                }}
                                            >
                                                {activeProductCTAText}
                                            </Adg4Button>
                                        </div>
                                    </div>
                                </div>
                                <div className="half text-section">
                                    {Teams.map(
                                        (team: TeamType, index: number) => (
                                            <HomepageSectionScrollItem
                                                key={index}
                                                index={index}
                                                brand={team.brand}
                                                quote={team.quote}
                                                customerCTAUrl={
                                                    team.customerCTAUrl
                                                }
                                                productName={team.productName}
                                                logo={team.logo}
                                                productCTA={team.productCTA}
                                                header={team.header}
                                                name={team.name}
                                                title={team.title}
                                                description={team.description}
                                                portrait={team.portrait}
                                                bgColor={team.bgColor}
                                                bgShape={team.bgShape}
                                                accentImg={team.accentImg}
                                                featureVideo={team.featureVideo}
                                                posterImage={team.posterImage}
                                            />
                                        )
                                    )}
                                </div>
                            </div>
                        </Row>
                    </div>
                </UFOSegment>
            </ScrollContext.Provider>
        </section>
    );
};
const scrollerStyles = css({
    "@keyframes fadein": {
        "0%": {
            opacity: 0
        },
        "100%": {
            opacity: 1
        }
    },
    "@keyframes bgshapepulseodd": {
        "0%": {
            transform: "scale(1)"
        },
        "50%": {
            transform: "scale(0.9)"
        },
        "100%": {
            transform: "scale(1)"
        }
    },
    "@keyframes bgshapepulseeven": {
        "0%": {
            transform: "scale(1)"
        },
        "50%": {
            transform: "scale(0.9)"
        },
        "100%": {
            transform: "scale(1)"
        }
    },
    "@keyframes strokeright": {
        "0%": {
            clipPath: "polygon(0 0, 0 0, 0 100%, 0 100%)"
        },
        "100%": {
            clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)"
        }
    },
    "@property --background-color-1": {
        syntax: "<color>",
        initialValue: "#fff",
        inherits: "false"
    },
    "--background-color-1": "#fff",
    "transition": "background-color 1s",
    "position": "relative",
    "margin": "20px 0 115px",
    "backgroundColor": "var(--background-color-1)",
    "backgroundImage":
        "linear-gradient(to bottom, white 40px, transparent 50px), linear-gradient(to top, white 100px, transparent 200px)",

    [media.below.sm]: {
        margin: "20px 0 50px"
    },

    ".row-width": {
        "position": "relative",

        [media.below.sm]: {
            "flexDirection": "column",
            "marginBlockEnd": "-40px",

            ".half": {
                "&.text-section": {
                    ".team-type": {
                        marginBlockEnd: "40px"
                    },
                    ".team-card": {
                        opacity: 1
                    },
                    ".img-region": {
                        "display": "block",
                        "margin": "50px auto 40px",
                        "padding": "15px",

                        ".img-accent": {
                            insetBlockEnd: "-11%",
                            insetInlineStart: "-6%"
                        },

                        [media.below.sm]: {
                            margin: "30px auto 40px"
                        }
                    },
                    ".cta-button": {
                        display: "block",
                        margin: "0 20px"
                    }
                },
                "&.img-section": {
                    display: "none"
                }
            }
        },
        [media.above.sm]: {
            ".header-wrapper": {
                position: "sticky",
                insetBlockStart: "0",
                zIndex: "2",
                clipPath: `polygon(0 0,100% 0,100% 100%,33% 100%,33% 37%,0 37%)`
            }
        },
        ".accent-heading": {
            background:
                "linear-gradient(180deg, var(--background-color-1) 90%, rgba(255,255,255, 0))",
            paddingBlockStart: "7vh",
            paddingBlockEnd: "20px",
            h2: {
                "fontSize": "2.5rem",
                "position": "relative",

                "&.is-visible svg": {
                    "path:nth-of-type(1)": {
                        animation: "fadein ease-out .2s 1 .2s forwards"
                    },
                    "path:nth-of-type(2)": {
                        animation: "fadein ease-out .2s 1 .4s forwards"
                    },
                    "path:nth-of-type(3)": {
                        animation: "fadein ease-out .2s 1 .6s forwards"
                    }
                },

                "svg": {
                    position: "absolute",
                    insetInlineStart: "60%",
                    insetBlockStart: "-75px",

                    path: {
                        opacity: 0
                    }
                }
            }
        },
        ".half": {
            [media.above.sm]: {
                "&.text-section": {
                    "width": "calc(35% - 15px)",
                    "position": "relative",
                    "insetBlockStart": "-240px",
                    ".img-region, .cta-button": {
                        display: "none"
                    }
                },
                "&.img-section": {
                    alignItems: "flex-end",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    flexBasis: "60%",
                    position: "sticky",
                    insetBlockStart: "0",
                    paddingBlockStart: "20px"
                },
                ".team-type": {
                    "position": "relative",
                    "marginBottom": "200px",

                    "&:first-child": {
                        marginBlockStart: "-45%"
                    },
                    "&:last-child": {
                        marginBlockEnd: "-45%"
                    }
                }
            },
            ".img-region": {
                "position": "relative",
                "marginBlockEnd": "40px",
                "aspectRatio": "7/4",
                "padding": "35px",
                "width": "100%",
                ".bg-shape": {
                    "width": "100%",
                    "height": "100%",
                    "transition": "all 1s",
                    "position": "absolute",
                    "insetInlineStart": 0,
                    "insetBlockStart": 0,
                    "&.odd": {
                        animation: "bgshapepulseodd ease-in-out 1s 1"
                    },
                    "&.even": {
                        animation: "bgshapepulseeven ease-in-out 1s 1"
                    }
                },
                "video, div, img": {
                    "&.img-screenshot": {
                        position: "relative",
                        display: "block",
                        height: "auto",
                        width: "100%",
                        margin: "auto",
                        animation: "fadein 0.25s 1",
                        borderRadius: "5px",
                        border: "3px solid #F0F1F2"
                    },
                    "&.img-accent": {
                        width: "20%",
                        position: "absolute",
                        insetInlineStart: "-3%",
                        insetBlockEnd: "-10%",

                        svg: {
                            "animation": "strokeright ease-in-out .5s 1",
                            "position": "relative",

                            "&.arrow-group-accent": {
                                insetInlineStart: "16px",
                                insetBlockStart: "-7px"
                            },
                            "&.lightning-accent-1": {
                                insetInlineStart: "-16px",
                                insetBlockStart: "-10px"
                            },
                            "&.squiggle-line-accent-1": {
                                insetInlineStart: "21px",
                                insetBlockStart: "-19px"
                            },
                            "&.squiggle-line-accent-2": {
                                insetInlineStart: "36px"
                            },
                            "&.star-group-accent": {
                                insetBlockStart: "-30px"
                            }
                        }
                    }
                }
            }
        }
    }
});
